import type { InfiniteData } from '@tanstack/react-query';

import type { DecompositionNode } from 'api/data/projects/types';

import type { DecompositionTreePage } from '../types';

/**
 * The four primitives both tree caches are built from — a sticky-top list is a plain
 * `DecompositionNode[]`, a paginated list is that array once per page.
 *
 * Every one returns the SAME reference when nothing matches. React-query treats an unchanged
 * reference as "no update", which is what keeps a redundant patch (the optimistic add,
 * `DataNodeCreated` and `DataRelationCreated` all patch the same parent) from re-rendering the tree.
 */

export function mapMatchingNodes(
  list: DecompositionNode[] | undefined,
  matches: (node: DecompositionNode) => boolean,
  transform: (node: DecompositionNode) => DecompositionNode
): DecompositionNode[] | undefined {
  if (!list || !list.some(matches)) return list;
  return list.map((node) => (matches(node) ? transform(node) : node));
}

export function removeMatchingNodes(
  list: DecompositionNode[] | undefined,
  matches: (node: DecompositionNode) => boolean
): DecompositionNode[] | undefined {
  if (!list || !list.some(matches)) return list;
  return list.filter((node) => !matches(node));
}

type Pages = InfiniteData<DecompositionTreePage, number> | undefined;

export function mapMatchingNodesInPages(
  pages: Pages,
  matches: (node: DecompositionNode) => boolean,
  transform: (node: DecompositionNode) => DecompositionNode
): Pages {
  return updatePages(pages, (items) => mapMatchingNodes(items, matches, transform));
}

export function removeMatchingNodesInPages(pages: Pages, matches: (node: DecompositionNode) => boolean): Pages {
  return updatePages(pages, (items) => removeMatchingNodes(items, matches));
}

function updatePages(
  pages: Pages,
  updateItems: (items: DecompositionNode[]) => DecompositionNode[] | undefined
): Pages {
  if (!pages) return pages;

  let changed = false;
  const nextPages = pages.pages.map((page) => {
    const items = updateItems(page.items);
    if (items === page.items || !items) return page;
    changed = true;
    return { ...page, items };
  });

  return changed ? { ...pages, pages: nextPages } : pages;
}
